import { SEED_INTENTS, type SeedIntent } from './seed-intents'
import { createBrowserDefaultEmbedder, embedText, similarityFor } from './embedding'
import type { IntentChunk, IntentChunkCandidate, TextEmbedder } from './types'

type IndexedSeedExample = {
  seed: SeedIntent
  text: string
  embedding: Float32Array
}

export interface LocalIntentRankerOptions {
  embedder?: TextEmbedder
  seeds?: SeedIntent[]
}

export class LocalIntentRanker {
  private readonly embedder: TextEmbedder
  private readonly seeds: SeedIntent[]
  private indexed: Promise<IndexedSeedExample[]> | null = null

  constructor(options: LocalIntentRankerOptions = {}) {
    this.embedder = options.embedder ?? createBrowserDefaultEmbedder()
    this.seeds = options.seeds ?? SEED_INTENTS
  }

  async rankChunk(chunk: IntentChunk, topK = 3): Promise<IntentChunkCandidate[]> {
    const queryEmbedding = chunk.embedding ?? (await embedText(this.embedder, chunk.normalizedText))
    chunk.embedding = queryEmbedding
    const indexed = await this.getIndexed()
    const grouped = new Map<string, IntentChunkCandidate>()

    for (const item of indexed) {
      const baseScore = similarityFor(this.embedder, queryEmbedding, item.embedding)
      const kindBoost = item.seed.kind === chunk.kind ? 0.08 : 0
      const score = Math.min(1, baseScore + kindBoost)
      const current = grouped.get(item.seed.canonicalId)
      if (!current || score > current.score) {
        grouped.set(item.seed.canonicalId, {
          canonicalId: item.seed.canonicalId,
          kind: item.seed.kind,
          score,
          ontologyRefs: item.seed.ontologyRefs,
          cogoJsonPaths: item.seed.cogoJsonPaths,
          mcpCandidates: item.seed.mcpCandidates,
        })
      }
    }

    return [...grouped.values()].sort((a, b) => b.score - a.score).slice(0, topK)
  }

  async enrichChunks(chunks: IntentChunk[], topK = 3): Promise<IntentChunk[]> {
    return await Promise.all(
      chunks.map(async (chunk) => {
        const candidates = await this.rankChunk(chunk, topK)
        const deterministicSeed = chunk.canonicalId
          ? this.seeds.find(
              (seed) => seed.canonicalId === chunk.canonicalId && seed.kind === chunk.kind,
            )
          : undefined
        if (deterministicSeed) {
          const ranked = candidates.find(
            (candidate) => candidate.canonicalId === deterministicSeed.canonicalId,
          )
          const deterministicCandidate: IntentChunkCandidate = {
            canonicalId: deterministicSeed.canonicalId,
            kind: deterministicSeed.kind,
            score: Math.max(0.72, ranked?.score ?? 0),
            ontologyRefs: deterministicSeed.ontologyRefs,
            cogoJsonPaths: deterministicSeed.cogoJsonPaths,
            mcpCandidates: deterministicSeed.mcpCandidates,
          }
          return {
            ...chunk,
            candidates: [
              deterministicCandidate,
              ...candidates.filter(
                (candidate) => candidate.canonicalId !== deterministicSeed.canonicalId,
              ),
            ].slice(0, topK),
            canonicalId: deterministicSeed.canonicalId,
            confidence: Math.max(chunk.confidence, deterministicCandidate.score),
            ontologyRefs: deterministicSeed.ontologyRefs ?? chunk.ontologyRefs,
            cogoJsonPaths: deterministicSeed.cogoJsonPaths ?? chunk.cogoJsonPaths,
            mcpCandidates: deterministicSeed.mcpCandidates ?? chunk.mcpCandidates,
          }
        }
        const best = candidates[0]
        if (!best) return { ...chunk, candidates: [] }
        const acceptsSemanticMapping =
          chunk.kind !== 'unknown' && best.kind === chunk.kind && best.score >= 0.45
        return {
          ...chunk,
          candidates,
          canonicalId: acceptsSemanticMapping ? best.canonicalId : chunk.canonicalId,
          confidence: Math.max(chunk.confidence, best.score),
          ontologyRefs: acceptsSemanticMapping
            ? (best.ontologyRefs ?? chunk.ontologyRefs)
            : chunk.ontologyRefs,
          cogoJsonPaths: acceptsSemanticMapping
            ? (best.cogoJsonPaths ?? chunk.cogoJsonPaths)
            : chunk.cogoJsonPaths,
          mcpCandidates: acceptsSemanticMapping
            ? (best.mcpCandidates ?? chunk.mcpCandidates)
            : chunk.mcpCandidates,
        }
      }),
    )
  }

  private getIndexed(): Promise<IndexedSeedExample[]> {
    this.indexed ??= this.buildIndex()
    return this.indexed
  }

  private async buildIndex(): Promise<IndexedSeedExample[]> {
    const rows: IndexedSeedExample[] = []
    for (const seed of this.seeds) {
      for (const text of seed.examples) {
        rows.push({
          seed,
          text,
          embedding: await embedText(this.embedder, text),
        })
      }
    }
    return rows
  }
}

let defaultRanker: LocalIntentRanker | null = null

export function getDefaultLocalIntentRanker(): LocalIntentRanker {
  defaultRanker ??= new LocalIntentRanker()
  return defaultRanker
}
